import { useState } from "react";
import classNames from "classnames";
import { DockTab, DockTabsProps } from "./DockTabs.types";

function panelClass(tab: DockTab, selected: boolean) {
  if (tab.below === "always") {
    return selected ? "d-block" : "d-block d-lg-none";
  }

  if (tab.below === "hide") {
    return selected ? "d-none d-lg-block" : "d-none";
  }

  return undefined;
}

/**
 * Tabs for a dock holding more than one panel, inside a `Dock`'s content rather than in
 * `Dock` itself. Every panel stays mounted, so switching tabs and back doesn't reset
 * what one was showing.
 *
 * The panels scroll, not the whole dock: the strip doesn't move, so a scrollbar beside
 * it was measuring something that never scrolls. Filling the dock's own scroller leaves
 * that one with nothing to do.
 */
export function DockTabs({
  tabs,
  open: controlledOpen,
  onOpen,
  hideStripBelowLg = false,
}: DockTabsProps) {
  const [ownOpen, setOwnOpen] = useState(tabs[0]?.label);
  const open = controlledOpen ?? ownOpen;
  const setOpen = (label: string) => {
    setOwnOpen(label);
    onOpen?.(label);
  };

  return (
    <div className="d-flex flex-column h-100 min-h-0">
      <div
        className={classNames("underline-tabs flex-shrink-0", {
          "d-none d-lg-flex": hideStripBelowLg,
        })}
        role="tablist"
      >
        {tabs.map((tab) => (
          <button
            key={tab.label}
            type="button"
            role="tab"
            aria-selected={tab.label === open}
            className={classNames("underline-tabs__tab", {
              "underline-tabs__tab--active": tab.label === open,
            })}
            onClick={() => setOpen(tab.label)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-grow-1 min-h-0 overflow-y-auto">
        {tabs.map((tab) => (
          <div
            key={tab.label}
            role="tabpanel"
            aria-label={tab.label}
            hidden={tab.below ? undefined : tab.label !== open}
            className={classNames(panelClass(tab, tab.label === open), { "h-100": tab.fill })}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
}

export default DockTabs;
